import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { RoleNames } from '@pg-app/core/services/roles/RoleNames';
import { ManageUserService } from '@pg-app/partner/manage-user/services/manage-user.service';
import { environment } from '@pg-env/environment';
import { ToastrService } from 'ngx-toastr';
import { MatDialog } from '@angular/material/dialog';
import { RegisterStep1Service } from '@pg-app/auth/services/register-step-1.service';
import { CommonDialogComponent } from '@pg-app/common/common-dialog/common-dialog.component';
import { ShareService } from '@nttp/common-service/share-service';
import { CommonMessages } from '@pg-app/shared/constants/common-messages';

@Component({
  selector: 'np-manage-user',
  templateUrl: './manage-user.component.html',
  styleUrls: ['./manage-user.component.scss']
})
export class ManageUserComponent implements OnInit {
  newUserForm: FormGroup;
  addNewUserHidden = true;
  partnerId = null;

  public selectTitle = [];
  MANAGE_USERS = RoleNames.MANAGE_USERS;
  brandName = environment.brandName;

  public permissionList = [];
  // user name can contains characters (a-z, A-Z, 0-9, dash -,  apostrophe ', space), no special characters
  namePattern = `([A-Za-z0-9\-' ]+)`;

  columns = [
    {
      title: 'First name',
      name: 'firstName',
      width: '200px'
    },
    {
      title: 'Last name',
      name: 'lastName',
      width: '200px'
    },
    {
      title: 'Email',
      name: 'email',
      width: '300px'
    },
    {
      title: 'Action',
      name: 'userStatus',
      hiddenSort: true,
      hiddenFilter: true,
      width: '130px'
    }
  ];

  public rows: Array<any> = [];
  commonMessage = CommonMessages;

  constructor(
    private fb: FormBuilder,
    private manageUserService: ManageUserService,
    private toasterService: ToastrService,
    private dialog: MatDialog,
    private registerService: RegisterStep1Service,
    private shareService: ShareService,
  ) {}

  ngOnInit() {
    this.newUserForm = this.fb.group({
      title: ['', Validators.required],
      firstName: ['', [Validators.required, Validators.maxLength(255), Validators.pattern(this.namePattern)]],
      middleName: ['', [Validators.maxLength(255), Validators.pattern(this.namePattern)]],
      lastName: ['', [Validators.required, Validators.maxLength(255), Validators.pattern(this.namePattern)]],
      email: ['', [Validators.required, Validators.email]]
    });

    this.getTitle();
    this.getPermission();
    this.getPartnerTable();
  }

  public addNewUser() {
    this.newUserForm.markAllAsTouched();
    if (this.newUserForm.valid) {
      if (this.partnerId) {
        this.addUpdateUser();
      } else {
        const param = {
          email: this.newUserForm.get('email').value
        };
        this.registerService.checkEmailExist(param).subscribe(res => {
          if (res.Exists) {
            this.toasterService.error(
              `This email address has already been registered with ${this.brandName}. Please contact one of the team on ${environment.phone} or signup@${environment.domain} to activate your account, mentioning error code Err101`
            );
          } else {
            this.addUpdateUser();
          }
        });
      }
    }
  }

  addUpdateUser() {
    const roles = this.permissionList.filter(item => item.checked);
    const permission = {
      roles: roles
    };

    const userForm = {
      id: this.partnerId,
      title: {
        id: this.newUserForm.get('title').value.id,
        title: this.newUserForm.get('title').value.name
      },
      firstName: this.newUserForm.get('firstName').value,
      middleName: this.newUserForm.get('middleName').value,
      lastName: this.newUserForm.get('lastName').value,
      email: this.newUserForm.get('email').value,
      clientId: environment.clientId,
      titleId: this.newUserForm.get('title').value.id,
      permission: permission
    };

    this.manageUserService.addNewUser(userForm).subscribe(
      data => {
        const message = this.partnerId
          ? 'User has been updated successfully!'
          : 'New user has been added successfully!';
        this.toasterService.success(message);
        this.resetForm();

        this.getPartnerTable();
        window.scrollTo(0, 0);
      }
    );
  }

  public showCreateUser() {
    this.addNewUserHidden = false;
    this.partnerId = null;
    setTimeout(() => {
      window.scrollTo({
        top: document.body.scrollHeight,
        behavior: 'smooth'
      });
    }, 100);
    this.resetForm();
  }

  resetForm() {
    this.newUserForm.reset();
    this.partnerId = null;
    this.permissionList.forEach(item => {
      item.checked = false;
    });
  }

  public getTitle() {
    this.shareService.titleList$.subscribe(data => {
      this.selectTitle = data;
    });
  }

  public getPermission() {
    this.manageUserService.getUserPermission().subscribe(data => {
      this.permissionList = data;
    });
  }

  public getPartnerTable() {
    this.manageUserService.getListTableUser().subscribe(data => {
      data = data || [];
      if (data.length > 0) {
        data.forEach(item => {
          item.userStatus = item.status === 1 ? '<span class="btn-action">Disable user</span>'
            : item.status === 3 ? '<span class="btn-action">Enable user</span>' : '';
        });
      }
      this.rows = data;
    });
  }

  showUserDetail(user) {
    const title = this.selectTitle.find(item => item.name === user.row.title);
    if (user.column === 'userStatus') {
      if (user.row.status !== 1 && user.row.status !== 3) {
        return;
      }
      this.showDialog(user, title);
    } else {
      this.parseUserInfo(user.row, title);
    }
  }

  showDialog(user, title) {
    const message =
      user.row.status === 1
        ? 'Are you sure you want to disable this user?'
        : 'Are you sure you want to enable this user?';
    const dialog = this.dialog.open(CommonDialogComponent, {
      disableClose: true,
      data: {
        title: message,
        dialogType: 3,
        routingUrl: null
      }
    });
    dialog.afterClosed().subscribe(result => {
      if (result) {
        const state = user.row.status === 1 ? 3 : 1;
        this.disabledUser(user, state, title);
      }
    });
  }

  parseUserInfo(user, title) {
    this.partnerId = user.id;
    const param = {
      userId: user.id
    };
    this.manageUserService.getUserRole(param).subscribe(data => {
      this.permissionList.forEach(item => {
        item.checked = data.indexOf(item.id) !== -1;
      });
    });

    this.addNewUserHidden = false;
    this.newUserForm.patchValue({
      title: title,
      firstName: user.firstName,
      middleName: user.middleName,
      lastName: user.lastName,
      email: user.email
    });

    setTimeout(() => {
      window.scrollTo({
        top: document.body.scrollHeight,
        behavior: 'smooth'
      });
    }, 100);
  }

  disabledUser(user, status, title) {
    const userForm = {
      id: user.row.id,
      status: status,
      title: {
        id: title.id,
        title: title.name
      },
      firstName: user.row.firstName,
      middleName: user.row.middleName ? user.row.middleName : null,
      lastName: user.row.lastName,
      email: user.row.email,
      clientId: environment.clientId
    };

    this.manageUserService.addNewUser(userForm).subscribe(
      data => {
        this.toasterService.success('User updated successfully');
        this.resetForm();
        this.getPartnerTable();
      }
    );
  }
}
